import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";
import type { PurchaseOrder, Location } from "../../types";

export default function ReceiveTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [receiving, setReceiving] = useState<Record<number, { qty: number; serialNumbers: string[] }>>({});
  const [currentSN, setCurrentSN] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [pos, locs] = await Promise.all([
        apiGet<PurchaseOrder[]>(`/organizations/${orgId}/purchase-orders`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
      ]);
      // Only show active POs (submitted or partial)
      setPurchaseOrders((pos as any[]).filter((p) => p.status === "submitted" || p.status === "partial"));
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const selectPO = (po: any) => {
    setSelectedPO(po);
    const init: Record<number, { qty: number; serialNumbers: string[] }> = {};
    (po.items || []).forEach((_: any, i: number) => { init[i] = { qty: 0, serialNumbers: [] }; });
    setReceiving(init);
    setStatus(null);
  };

  const updateQty = (idx: number, qty: number) => {
    const item = selectedPO.items[idx];
    const remaining = item.expectedQty - item.receivedQty;
    setReceiving((prev) => ({ ...prev, [idx]: { ...prev[idx], qty: Math.min(Math.max(0, qty), remaining) } }));
  };

  const addSerialNumber = (idx: number) => {
    if (!currentSN.trim()) return;
    setReceiving((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], serialNumbers: [...prev[idx].serialNumbers, currentSN.trim()] },
    }));
    setCurrentSN("");
  };

  const removeSerialNumber = (idx: number, snIdx: number) => {
    setReceiving((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], serialNumbers: prev[idx].serialNumbers.filter((_, i) => i !== snIdx) },
    }));
  };

  const canSubmit = () => {
    if (!selectedPO) return false;
    let hasAny = false;
    for (const [idx, recv] of Object.entries(receiving)) {
      const item = selectedPO.items[parseInt(idx)];
      if (recv.qty > 0) {
        hasAny = true;
        // If requires serial, must have exactly qty serial numbers
        if (item.requiresSerialNumber && recv.serialNumbers.length !== recv.qty) {
          return false;
        }
      }
    }
    return hasAny;
  };

  const handleReceive = async () => {
    if (!canSubmit()) return;
    setSubmitting(true);
    setStatus(null);

    try {
      // Update PO items with received quantities
      const updatedItems = selectedPO.items.map((item: any, idx: number) => {
        const recv = receiving[idx];
        return {
          ...item,
          receivedQty: item.receivedQty + recv.qty,
          serialNumbers: [...(item.serialNumbers || []), ...recv.serialNumbers],
        };
      });

      // Determine new PO status
      const allReceived = updatedItems.every((i: any) => i.receivedQty >= i.expectedQty);
      const anyReceived = updatedItems.some((i: any) => i.receivedQty > 0);

      await apiPut(`/organizations/${orgId}/purchase-orders/${selectedPO.purchaseOrderId}`, {
        ...selectedPO,
        items: updatedItems,
        status: allReceived ? "received" : anyReceived ? "partial" : selectedPO.status,
      });

      // Also update inventory quantities
      for (const [idx, recv] of Object.entries(receiving)) {
        if (recv.qty > 0) {
          const item = selectedPO.items[parseInt(idx)];
          try {
            const existing = await apiGet<any[]>(`/organizations/${orgId}/inventory/${item.productId}`);
            const current = (existing || []).find((i: any) => i.locationId === selectedPO.locationId);
            const currentQty = current?.quantity || 0;
            await apiPut(`/organizations/${orgId}/inventory/${item.productId}/${selectedPO.locationId}`, {
              quantity: currentQty + recv.qty,
            });
          } catch { /* first time inventory for this product */ 
            await apiPut(`/organizations/${orgId}/inventory/${item.productId}/${selectedPO.locationId}`, {
              quantity: recv.qty,
            });
          }
        }
      }

      const totalReceived = Object.values(receiving).reduce((s, r) => s + r.qty, 0);
      setStatus({ type: "success", message: `Received ${totalReceived} items against ${selectedPO.poNumber}${allReceived ? " — PO complete!" : ""}` });
      setSelectedPO(null);
      await loadData();
    } catch (err) {
      setStatus({ type: "error", message: err instanceof Error ? err.message : "Failed to receive" });
    } finally {
      setSubmitting(false);
    }
  };

  const getLocName = (id: string) => (locations.find((l: any) => l.locationId === id) as any)?.name || id;

  if (loading) return <div className="empty-state">Loading...</div>;

  if (!selectedPO) {
    return (
      <div>
        <div className="org-mgmt-header"><h2>Receive Inventory</h2></div>

        {status && <div style={{ marginBottom: "1rem" }}><span className={`save-status ${status.type}`}>{status.message}</span></div>}

        {purchaseOrders.length === 0 ? (
          <div className="empty-state">
            <p>No active purchase orders. Create a PO first, then receive against it.</p>
          </div>
        ) : (
          <div>
            <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: "1rem" }}>Select a purchase order to receive against:</p>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {purchaseOrders.map((po: any) => (
                <div key={po.purchaseOrderId} onClick={() => selectPO(po)} style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "1rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "border-color 0.15s" }} onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#2563eb")} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{po.poNumber} — {po.vendor}</div>
                    <div style={{ color: "#6b7280", fontSize: "0.85rem" }}>{po.items?.length || 0} items → {getLocName(po.locationId)}</div>
                  </div>
                  <span style={{ padding: "0.15rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 500, background: po.status === "partial" ? "#fef3c7" : "#eff6ff", color: po.status === "partial" ? "#92400e" : "#1e40af" }}>{po.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Receiving: {selectedPO.poNumber}</h2>
        <button className="btn-cancel" onClick={() => setSelectedPO(null)} style={{ padding: "0.4rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", background: "white", cursor: "pointer" }}>← Back</button>
      </div>

      <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "1rem" }}>
        Vendor: {selectedPO.vendor} | Location: {getLocName(selectedPO.locationId)}
      </p>

      {status && <div style={{ marginBottom: "1rem" }}><span className={`save-status ${status.type}`}>{status.message}</span></div>}

      <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
        {(selectedPO.items || []).map((item: any, idx: number) => {
          const remaining = item.expectedQty - item.receivedQty;
          const recv = receiving[idx] || { qty: 0, serialNumbers: [] };
          if (remaining <= 0) return (
            <div key={idx} style={{ padding: "0.75rem", borderBottom: "1px solid #f3f4f6", background: "#f9fafb", opacity: 0.6 }}>
              <span style={{ fontWeight: 500 }}>{item.productName}</span> — <span style={{ color: "#16a34a" }}>Fully received ✓</span>
            </div>
          );
          return (
            <div key={idx} style={{ padding: "0.75rem", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{item.productName}</div>
                  <div style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                    Expected: {item.expectedQty} | Already received: {item.receivedQty} | Remaining: {remaining}
                    {item.requiresSerialNumber && <span style={{ color: "#7c3aed", marginLeft: "0.5rem" }}>SN Required</span>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                  <span style={{ fontSize: "0.8rem" }}>Receiving:</span>
                  <input type="number" min="0" max={remaining} value={recv.qty} onChange={(e) => updateQty(idx, parseInt(e.target.value) || 0)} style={{ width: "60px", padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: "4px", textAlign: "center" }} />
                </div>
              </div>

              {/* Serial number entry for items that require it */}
              {item.requiresSerialNumber && recv.qty > 0 && (
                <div style={{ marginTop: "0.5rem", paddingLeft: "0.5rem", borderLeft: "3px solid #7c3aed" }}>
                  <div style={{ fontSize: "0.8rem", color: "#7c3aed", marginBottom: "0.3rem" }}>
                    Serial numbers: {recv.serialNumbers.length} / {recv.qty} required
                  </div>
                  <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.3rem" }}>
                    <input value={currentSN} onChange={(e) => setCurrentSN(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSerialNumber(idx); } }} placeholder="Scan serial number..." style={{ flex: 1, padding: "0.35rem 0.5rem", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85rem", fontFamily: "monospace" }} />
                    <button type="button" onClick={() => addSerialNumber(idx)} style={{ padding: "0.35rem 0.6rem", background: "#7c3aed", color: "white", border: "none", borderRadius: "4px", fontSize: "0.8rem", cursor: "pointer" }}>Add</button>
                  </div>
                  {recv.serialNumbers.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                      {recv.serialNumbers.map((sn, snIdx) => (
                        <span key={snIdx} style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.15rem 0.4rem", background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: "4px", fontSize: "0.75rem", fontFamily: "monospace" }}>
                          {sn}
                          <button type="button" onClick={() => removeSerialNumber(idx, snIdx)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "0.7rem" }}>✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "1.25rem", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={handleReceive} disabled={!canSubmit() || submitting} className="btn btn-primary" style={{ width: "auto" }}>
          {submitting ? "Processing..." : "Confirm Receive"}
        </button>
      </div>
    </div>
  );
}
